import { ActivatedRoute } from '@angular/router';
import { Component, Input, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { Match } from '../../_shared/models/match.model';
import { Server } from '../../_shared/models/point.model';
import { Tie } from '../../_shared/models/tie.model';
import { User } from '../../_shared/models/user.model';
import { END_POINT } from '../../../environments/environment';
import { ScoreboardService } from '../scoreboard.service';
import { ClockName } from '../../_shared/models/enums/clock-name.enum';
import { SocketService } from '../../_core/services/socket.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Location } from '@angular/common';
import { Preferences } from '@capacitor/preferences';

@Component({
  standalone: false,
  selector: 'app-themed-stream-view',
  templateUrl: './themed-stream-view.component.html',
  styleUrls: ['./themed-stream-view.component.scss'],
})
export class ThemedStreamViewComponent implements OnInit, OnDestroy {
  public scoreboardService = inject(ScoreboardService);
  public activatedRoute = inject(ActivatedRoute);
  public socketService = inject(SocketService);
  public location = inject(Location);

  @Input() match: Match;
  @Input() server: Server;
  @Input() courtName: string;
  @Input() isRetiredOrIncomplete: boolean;
  @Input() showPoints: boolean;
  @Input() side1Color: string;
  @Input() side2Color: string;
  @Input() pointScoreSide1: string;
  @Input() pointScoreSide2: string;
  @Input() statusSide1: string;
  @Input() statusSide2: string;
  @Input() tie: Tie;

  link: string;
  theme: string;
  logo: string;
  setTimer: import('../../_shared/models/clock.model').Clock;
  pointBreakTimer: import('../../_shared/models/clock.model').Clock;
  score: number;
  private readonly destroy$: Subject<void> = new Subject();
  isIndividualMatch: boolean | import("../../_shared/models/clock.model").Clock;
  deviceId: string;

  constructor() { }

  async ngOnInit() {
    this.theme = this.activatedRoute.snapshot.params.theme;
    this.logo = this.getLogo();
    this.deviceId = (await Preferences.get({ key: 'deviceId' }))?.value;
    document.body.classList.add('transparent');
    this.link = this.match.tie
      ? `${END_POINT}/ties/${this.match.tie}`
      : `${END_POINT}/live-scoring/${this.match._id}`;

    this.isIndividualMatch = this.location.path().includes('live-scoring');

    this.setTimer = this.match.clocks.find(
      (c) => c.name === ClockName.SET_TIME
    );
    this.pointBreakTimer = this.match.clocks.find(
      (c) => c.name === ClockName.POINT_BREAK
    );

    this.socketService.getSocketUpdate('match/score')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if (data && data?._id === this.match._id && (!this.deviceId || !data.deviceId || data.deviceId !== this.deviceId)) {
          this.server = data.score.server;
          this.match.matchStatus = data.matchStatus;
          this.match.winningSide = data.winningSide;
        }
      })

  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
}

  private getScore(score: string): string[] {
    /**
     * Retrieves the score of the match.
     * - If `score` is a string, it splits it using '-' as a delimiter.
     * - If `score` is not a string (i.e., initially an object before updates arrive via socket),
     *   it falls back to `this.tie.score` from the info API response.
     *
     * @param score - The current score, either as a string or an object (before socket updates).
     * @returns An array containing the split score values.
     */
    return typeof score === 'string'
      ? score.split('-')
      : this.tie.score.split('-');
  }

  private getLogo(): string {
    switch (this.theme) {
      case 'default':
        return 'assets/img/logo/ioncourt/logo-square.png';
        break;

      // case 'ncaa':
      //   return 'assets/img/logo/ncaa/logo.png';
      //   break;

      default:
        return null;
        break;
    }
  }

  hexToRgba(hex: string, alpha: number = 1): string {
    if (!hex) return 'rgba(0, 0, 0, 1)';

    hex = hex.replace(/^#/, '');

    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }


  getParticipantName(person: User, sideNumber: number): string {
    let participantName = '';
    if (person) {
      participantName += `${person.first_name.charAt(
        0
      )}. ${person.last_name?.toUpperCase()}`;
    } else {
      participantName += this.tie
        ? `${this.getTeamName(sideNumber)} ${this.match.collectionPosition}`
        : `Player ${sideNumber}`;
    }

    return participantName;
  }

  getLastNames(sideNumber: number): string {
    let participantName = '';
    this.match.sides[sideNumber - 1].players?.forEach((player, index) => {
      if (index === 1) {
        participantName += ' & ';
      }

      const lastName = player?.participant
        ? player?.participant.last_name
        : 'TBD';
      participantName += lastName;
    });

    return participantName;
  }

  getTeamName(sideNumber) {
    if (this.tie?.sides[sideNumber - 1]?.team) {
      return `${this.tie?.sides[sideNumber - 1]?.team.abbreviation}`;
    }

    return null;
  }

  setsWon(sideNumber) {
    let setsWon = 0;
    this.match.score.sets.forEach((set) => {
      if (set.winningSide === sideNumber) setsWon++;
    });

    return setsWon;
  }

  getWinningTeamColor(tie?: Tie) {
    if (!this.match.winningSide) return;
    const winningSIDEIndex = this.match.winningSide - 1;
    return this.hexToRgba(tie?.sides[winningSIDEIndex].team.colors[0], 0.6);
  }
}
